import { useState, useEffect, useCallback } from "react";
import { ExternalLink, Eye, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { api, URLReview, ApiClientError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export function URLReviewQueue() {
  const { logout } = useAuth();
  const [reviewQueue, setReviewQueue] = useState<URLReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [classification, setClassification] = useState("");
  const [secondaryFlags, setSecondaryFlags] = useState({
    captcha: false,
    regional: false,
    slow: false,
    scraper: false,
  });
  const [notes, setNotes] = useState("");
  const [showHtml, setShowHtml] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadReviewQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.urls.getReviewQueue();
      setReviewQueue(data);
      setCurrentIndex(0); // Reset to first item when queue loads
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          // Show the actual error detail for debugging
          const errorMsg = err.detail || `Failed to load review queue (${err.status})`;
          console.error("Review queue error:", err);
          setError(errorMsg);
        }
      } else {
        console.error("Review queue error:", err);
        setError(`Failed to load review queue: ${err instanceof Error ? err.message : String(err)}`);
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    loadReviewQueue();
  }, [loadReviewQueue]);

  const currentURL = reviewQueue.length > 0 && currentIndex < reviewQueue.length 
    ? reviewQueue[currentIndex] 
    : null;
  const remainingCount = reviewQueue.length > currentIndex 
    ? reviewQueue.length - currentIndex 
    : 0;

  const handleConfirmDown = async () => {
    if (!currentURL || !classification) return;
    
    setSubmitting(true);
    try {
      await api.urls.submitReview(
        currentURL.id,
        classification,
        secondaryFlags,
        notes || null,
        "confirm_down"
      );
      
      // Remove reviewed URL from queue and reload
      await loadReviewQueue();
      resetForm();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to submit review");
        }
      } else {
        setError("Failed to submit review");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkLive = async () => {
    if (!currentURL || !classification) return;
    
    setSubmitting(true);
    try {
      await api.urls.submitReview(
        currentURL.id,
        classification,
        secondaryFlags,
        notes || null,
        "mark_live"
      );
      
      // Remove reviewed URL from queue and reload
      await loadReviewQueue();
      resetForm();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to submit review");
        }
      } else {
        setError("Failed to submit review");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setClassification("");
    setSecondaryFlags({
      captcha: false,
      regional: false,
      slow: false,
      scraper: false,
    });
    setNotes("");
    setShowHtml(false);
  };

  const handleOpenURL = () => {
    if (currentURL) {
      window.open(currentURL.url, "_blank", "noopener,noreferrer");
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-0 flex-1">
        <PageHeader 
          title="URL Review Queue" 
          subtitle="Review system-detected takedowns for accuracy" 
        />
        <div className="text-center py-12">
          <div className="text-lg text-muted-foreground">Loading review queue...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-0 flex-1">
        <PageHeader 
          title="URL Review Queue" 
          subtitle="Review system-detected takedowns for accuracy" 
        />
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded">
          {typeof error === "string" ? error : "An error occurred"}
        </div>
      </div>
    );
  }

  if (!loading && reviewQueue.length === 0) {
    return (
      <div className="flex flex-col min-h-0 flex-1">
        <PageHeader 
          title="URL Review Queue" 
          subtitle="Review system-detected takedowns for accuracy" 
        />
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            Review Queue Empty
          </h2>
          <p className="text-muted-foreground">All URLs have been reviewed!</p>
        </div>
      </div>
    );
  }

  if (!currentURL) {
    return (
      <div className="flex flex-col min-h-0 flex-1">
        <PageHeader 
          title="URL Review Queue" 
          subtitle="Review system-detected takedowns for accuracy" 
        />
        <div className="text-center py-12 text-muted-foreground">
          {loading ? "Loading..." : "No URL selected"}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <PageHeader 
        title="URL Review Queue" 
        subtitle={`Review system-detected takedowns for accuracy • ${remainingCount} remaining`}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3 ml-auto">
          <Badge
            variant="outline"
            className="bg-orange-50 text-orange-800 border-orange-200"
          >
            {currentIndex + 1} of {reviewQueue.length}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setCurrentIndex(
                Math.min(reviewQueue.length - 1, currentIndex + 1)
              )
            }
            disabled={currentIndex === reviewQueue.length - 1}
          >
            Skip
          </Button>
        </div>
      </PageHeader>

      <div className="bg-card rounded-lg border flex-1 overflow-auto">
        <div className="bg-card rounded-lg border flex-1 overflow-auto">
          {/* URL Info Section */}
          <div className="p-6 border-b">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="text-sm text-muted-foreground mb-1">URL</div>
                <div className="text-sm text-foreground break-all mb-2">
                  {currentURL.url}
                </div>
              </div>
              <Button onClick={handleOpenURL} variant="outline" size="sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Window
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Case</div>
                <div className="text-sm text-foreground">{currentURL.case}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Agency</div>
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-800 border-blue-200"
                >
                  {currentURL.agency}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  Last Known Live
                </div>
                <div className="text-sm text-foreground">
                  {formatDate(currentURL.lastLiveDate)}
                </div>
              </div>
            </div>
          </div>

          {/* System Detection Section */}
          <div className="p-6 border-b bg-muted">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  System Detected
                </div>
                <Badge
                  variant="outline"
                  className="bg-red-100 text-red-800 border-red-200"
                >
                  {currentURL.detectedIssue}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  Raw Error
                </div>
                <div className="text-sm text-foreground font-mono">
                  {currentURL.rawError}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  Detected Date
                </div>
                <div className="text-sm text-foreground">
                  {formatDate(currentURL.detectedDate)}
                </div>
              </div>
            </div>

            {currentURL.capturedHtml && (
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHtml(!showHtml)}
              >
                <Eye className="w-4 h-4 mr-2" />
                {showHtml ? "Hide" : "View"} Captured HTML
              </Button>
              {showHtml && (
                <div className="mt-2 bg-card border rounded p-3 max-h-40 overflow-y-auto">
                  <pre className="text-xs text-foreground whitespace-pre-wrap">
                    {currentURL.capturedHtml}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Human Classification Section */}
        <div className="p-6 border-b">
          <h3 className="text-sm font-medium text-foreground mb-4">
            Human Classification
          </h3>
          <div className="text-sm text-muted-foreground mb-3">Choose one:</div>

          <RadioGroup value={classification} onValueChange={setClassification}>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="up-image-present"
                  id="up-image-present"
                />
                <Label htmlFor="up-image-present" className="cursor-pointer">
                  Page is up; image is still present
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="up-image-gone" id="up-image-gone" />
                <Label htmlFor="up-image-gone" className="cursor-pointer">
                  Page is up; image is gone
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="up-victim-data"
                  id="up-victim-data"
                />
                <Label htmlFor="up-victim-data" className="cursor-pointer">
                  Page is up; image gone but victim data visible
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="page-down" id="page-down" />
                <Label htmlFor="page-down" className="cursor-pointer">
                  Page is down (HTTP error or blank)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="domain-down" id="domain-down" />
                <Label htmlFor="domain-down" className="cursor-pointer">
                  Domain is down (DNS error / host unreachable)
                </Label>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Secondary Flags Section */}
        <div className="p-6 border-b bg-muted">
          <h3 className="text-sm font-medium text-foreground mb-4">
            Secondary Flags (optional)
          </h3>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="captcha"
                checked={secondaryFlags.captcha}
                onCheckedChange={(checked) =>
                  setSecondaryFlags({
                    ...secondaryFlags,
                    captcha: checked as boolean,
                  })
                }
              />
              <Label htmlFor="captcha" className="cursor-pointer">
                CAPTCHA / Interstitial
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="regional"
                checked={secondaryFlags.regional}
                onCheckedChange={(checked) =>
                  setSecondaryFlags({
                    ...secondaryFlags,
                    regional: checked as boolean,
                  })
                }
              />
              <Label htmlFor="regional" className="cursor-pointer">
                Page loads but image only blocked regionally
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="slow"
                checked={secondaryFlags.slow}
                onCheckedChange={(checked) =>
                  setSecondaryFlags({
                    ...secondaryFlags,
                    slow: checked as boolean,
                  })
                }
              />
              <Label htmlFor="slow" className="cursor-pointer">
                Site extremely slow / times out intermittently
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="scraper"
                checked={secondaryFlags.scraper}
                onCheckedChange={(checked) =>
                  setSecondaryFlags({
                    ...secondaryFlags,
                    scraper: checked as boolean,
                  })
                }
              />
              <Label
                htmlFor="scraper"
                className="cursor-pointer text-orange-800"
              >
                Problem with our scraper (report to engineering)
              </Label>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className="p-6 border-b">
          <Label htmlFor="notes" className="mb-2 block">
            Notes
          </Label>
          <Textarea
            id="notes"
            placeholder="Add any additional context or observations..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </div>

        {/* Final Actions Section */}
        <div className="p-6 bg-muted">
          <h3 className="text-sm font-medium text-foreground mb-4">
            Final Actions
          </h3>
          <div className="flex gap-3">
            <Button
              onClick={handleConfirmDown}
              disabled={!classification || submitting || !currentURL}
              className="flex-1"
            >
              {submitting ? "Submitting..." : "Confirm as Down"}
            </Button>
            <Button
              onClick={handleMarkLive}
              disabled={!classification || submitting || !currentURL}
              variant="outline"
              className="flex-1"
            >
              {submitting ? "Submitting..." : "Mark as Still Live"}
            </Button>
          </div>
          {!classification && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              Please select a classification before proceeding
            </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

